import {
  FilterOps, ICompositeFilterClause, IFieldFilterClause, ILeafFilterClause,
  IStringArrayValuedFieldFilterClause, IStringValuedFilterClause, IValuedFieldFilterClause
} from '../api/types/IFilterClause';

export class Filter implements ICompositeFilterClause {
  public operator: string;
  public clauses: ILeafFilterClause[];

  public constructor(compositeFilterClause: ICompositeFilterClause = { operator: FilterOps.and, clauses: [] }) {
    this.operator = compositeFilterClause.operator;
    this.clauses = compositeFilterClause.clauses;
  }

  public and(...leafFilterClause: ILeafFilterClause[]): Filter {
    return new Filter({
      operator: FilterOps.and,
      clauses: this.clauses.concat(leafFilterClause)
    });
  }

  public findFieldFilterClause(field: string, operator?: string): IFieldFilterClause | void {
    let clauses = this.findFieldFilterClauses(field, operator);
    return (clauses.length === 1) ? clauses[0] : undefined;
  }

  public findFieldFilterClauses(field: string, operator?: string): IFieldFilterClause[] {
    return this.clauses
      .filter(Filter.isFieldFilterClause)
      .map(clause => <IFieldFilterClause> clause) // Convert typing for TypeScript
      .filter(clause => (clause.field === field) && (!operator || clause.operator === operator));
  }

  public static and(...clauses: ILeafFilterClause[]): Filter {
    return new Filter({ operator: FilterOps.and, clauses });
  }

  public static search(search: string): IStringValuedFilterClause {
    return {
      operator: FilterOps.matches,
      value: search
    };
  }

  public static fieldEq<T>(field: string, value: T): IValuedFieldFilterClause<T> {
    return {
      operator: FilterOps.eq,
      field: field,
      value: value
    };
  }

  public static stringFieldEq(field: string, value: string): IValuedFieldFilterClause<string> {
    return Filter.fieldEq(field, value);
  }

  public static numberFieldEq(field: string, value: number): IValuedFieldFilterClause<number> {
    return Filter.fieldEq(field, value);
  }

  public static stringFieldGte(field: string, value: string): IValuedFieldFilterClause<string> {
    return {
      operator: FilterOps.gte,
      field: field,
      value: value
    };
  }

  public static stringFieldLte(field: string, value: string): IValuedFieldFilterClause<string> {
    return {
      operator: FilterOps.lte,
      field: field,
      value: value
    };
  }

  public static stringFieldMatches(field: string, value: string): IValuedFieldFilterClause<string> {
    return {
      operator: FilterOps.matches,
      field: field,
      value: value
    };
  }

  public static stringArrayFieldHas(field: string, value: string): IValuedFieldFilterClause<string> {
    return {
      operator: FilterOps.has,
      field: field,
      value: value
    };
  }

  public static stringFieldInArray(field: string, values: string[]): IStringArrayValuedFieldFilterClause {
    return {
      operator: FilterOps.inArray,
      field: field,
      values: values
    };
  }

  public static findFieldFilterClause(
    field: string,
    operator: string,
    filter: ICompositeFilterClause): IFieldFilterClause | void {

    return (new Filter(filter)).findFieldFilterClause(field, operator);
  }

  public static isFieldFilterClause(clause: ILeafFilterClause): clause is IFieldFilterClause {
    return !!(<IFieldFilterClause> clause).field;
  }
}
